import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, List, Tooltip, Typography } from 'antd';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';

import { RTU_ICONS } from '../../constants/rtuIcons';
import { defaultThemeColor } from '../../config/themeConfig';
import { addAction, announceJustLaunchedAction, hydrateActionSnapshots } from '../../redux/slices/actionsSlice';
import { fetchRTUData, fetchRTUs } from '../../redux/slices/rtuSlice';
import { AppDispatch } from '../../redux/store/store';
import {
  createActionIdempotencyKey,
  fetchActionLaunchRequest,
  fetchRtuActionHints,
  isRtuTargetIdentityChangedError,
  isUnknownLaunchOutcome,
  launchDeploymentDownload,
  launchConfigDownload,
  launchRebootRtus,
  launchRtuDebugLogDownload,
  launchRtuInfoRtus,
  launchUploadConfig,
  launchUploadDeployment,
  launchUploadLicense,
  RtuActionHint,
} from '../../services/rtuService';
import {
  createPendingActionRequestFingerprint,
  findPendingActionLaunch,
  PendingActionLaunchType,
  removePendingActionLaunch,
  savePendingActionLaunch,
} from '../../services/pendingActionLaunchStorage';
import { Action, ActionTargetReference } from '../../types/actionTypes';
import { RTU } from '../../types/RTU';
import { resolveRtuSerialNo } from '../../utils/rtuIdentity';
import { useCapabilityAccess } from '../../hooks/useCapabilityAccess';
import IconActionMenuButton from '../common/IconActionMenuButton';
import RtuPingDialog from './RtuPingDialog';
import RtuGenerateLicenceDialog from './RtuGenerateLicenceDialog';
import ProgressDialog from './ProgressDialog/ProgressDialog';
import { ASSET_INLINE_ACTIONS, DEFAULT_PINNED_ROW_ACTIONS, ROW_ACTION_CATALOG, RowActionKey } from './actionCatalog';
import {
  ActionConfirmationModal,
  ActionFeedbackKind,
  ActionFeedbackModal,
  DeploymentUpdateModal,
  DownloadActionModal,
  QuickActionsModal,
  UploadActionModal,
} from '../../features/actions';

import './RTUActions.css';

interface RTUActionsProps {
  mode: 'row' | 'bulk';
  selectedRTUIp?: string | null; // Display compatibility only. Atomic commands use serialNo.
  selectedRows: RTU[];
  surface?: 'assets' | 'detail';
  syncListStateOnPing?: boolean;
}

type ActionModalKind = 'config' | 'deploy' | 'license' | 'downloadConfig' | 'downloadDebugLog' | 'downloadDeployment' | 'reboot' | 'rtuInfo' | null;

interface FeedbackState {
  actionLabel?: string;
  kind: ActionFeedbackKind;
  message: string;
  onAction?: () => void;
  onClose?: () => void;
  open: boolean;
  title: string;
}

interface PendingLaunch {
  actionType: PendingActionLaunchType;
  fallbackName: string;
  idempotencyKey: string;
  requestFingerprint: string;
  targets: ActionTargetReference[];
  submit: (idempotencyKey: string) => Promise<Action>;
}

interface HintsResolution {
  // Readiness key = `${accessIdentityKey}|${targetIdsKey}` - deliberately
  // excludes the evidence version, so a version-only permission refresh for
  // the same identity/selection doesn't read as a new (not-yet-ready) key.
  key: string;
  status: 'loading' | 'error' | 'ready';
  hints: Record<number, RtuActionHint>;
}

const EMPTY_HINTS_RESOLUTION: HintsResolution = { key: '', status: 'ready', hints: {} };
const EMPTY_FEEDBACK: FeedbackState = { kind: 'info', message: '', open: false, title: '' };
const QUICK_ACTIONS_STORAGE_KEY = 'dms.rtu.quick-actions';
const MAX_DETAIL_INLINE_ACTIONS = 4;
const REJECTED = new Set(['FAILED', 'FAILURE', 'CANCELLED', 'CANCELED', 'BLOCKED', 'SKIPPED', 'TIMED_OUT']);

const parsePinnedActionKeys = (value: string | null): RowActionKey[] => {
  if (!value) return [...DEFAULT_PINNED_ROW_ACTIONS];
  try {
    const allowed = new Set<RowActionKey>(ROW_ACTION_CATALOG.map((item) => item.key));
    const keys = JSON.parse(value);
    const sanitized = Array.isArray(keys) ? keys.filter((key): key is RowActionKey => typeof key === 'string' && allowed.has(key as RowActionKey)) : [];
    return sanitized.length ? sanitized : [...DEFAULT_PINNED_ROW_ACTIONS];
  } catch {
    return [...DEFAULT_PINNED_ROW_ACTIONS];
  }
};

const detailsRejected = (action: Action) => action.actionDetails.filter((detail) => REJECTED.has(String(detail.status).toUpperCase()));
const detailsAccepted = (action: Action) => action.actionDetails.filter((detail) => !REJECTED.has(String(detail.status).toUpperCase()));

const getActionTargets = (mode: 'row' | 'bulk', rows: RTU[]) => mode === 'bulk' ? rows : rows.slice(0, 1);

const fileRequestIdentity = (file: File) => ({
  lastModified: file.lastModified,
  name: file.name,
  size: file.size,
  type: file.type,
});

const RTUActions: React.FC<RTUActionsProps> = ({
  mode,
  selectedRows,
  surface = 'assets',
  syncListStateOnPing = true,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const capabilityAccess = useCapabilityAccess();
  // Ping is Operator+Administrator; every other Action (update/download/reboot/
  // rtuInfo/generateLicence) is Administrator-only. UI hiding is presentation
  // only - the backend independently enforces RTU_MANUAL_PING/RTU_ACTION_EXECUTE.
  const canUseDmsPing = capabilityAccess.canUseDmsOperatorFeatures;
  const canExecuteDmsActions = capabilityAccess.canUseDmsActions;
  // Identity + a version that changes on every successful permission refresh
  // (see useCapabilityAccess) - null while there's no fresh evidence to act
  // on (still loading, logged out). Hints revalidate against these instead of
  // the selectedRows-derived arrays below, so a WebSocket-driven re-render
  // that only reallocates those arrays never triggers a refetch.
  const accessIdentityKey = capabilityAccess.accessIdentityKey;
  const accessEvidenceVersion = capabilityAccess.accessEvidenceVersion;
  const singleMenu = useRef<Menu>(null);
  const bulkMenu = useRef<Menu>(null);
  const [activeModal, setActiveModal] = useState<ActionModalKind>(null);
  const [feedback, setFeedback] = useState<FeedbackState>(EMPTY_FEEDBACK);
  const [launching, setLaunching] = useState(false);
  const [browserUploadPercent, setBrowserUploadPercent] = useState<number | null>(null);
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const [pingDialogVisible, setPingDialogVisible] = useState(false);
  const [licenceDialogVisible, setLicenceDialogVisible] = useState(false);
  const [progressDialogVisible, setProgressDialogVisible] = useState(false);
  const [actionId, setActionId] = useState<number | null>(null);
  const [actionName, setActionName] = useState('');
  const [actionTimestamp, setActionTimestamp] = useState('');
  const [pinnedActionKeys, setPinnedActionKeys] = useState<RowActionKey[]>(() => parsePinnedActionKeys(window.localStorage.getItem(QUICK_ACTIONS_STORAGE_KEY)));
  const inFlightLaunchRef = useRef<PendingLaunch | null>(null);
  const reconciliationInFlightRef = useRef(false);
  const [hintsResolution, setHintsResolution] = useState<HintsResolution>(EMPTY_HINTS_RESOLUTION);

  const targets = useMemo(
    () => getActionTargets(mode, selectedRows).map((rtu) => ({
      ...rtu,
      serialNo: resolveRtuSerialNo(rtu),
    })),
    [mode, selectedRows],
  );

  const targetIds = useMemo(
    () => targets.map((rtu) => rtu.id).filter((id): id is number => typeof id === 'number' && id > 0),
    [targets],
  );

  // Order-independent key for the current selection, so staleness can be
  // detected by comparison alone - in render, not only once an effect runs -
  // closing the gap where a stale selection's hints could otherwise flash as
  // enabled for one frame after the selection changes but before the effect
  // below fires.
  const targetIdsKey = useMemo(() => [...targetIds].sort((a, b) => a - b).join(','), [targetIds]);

  // targetIds is read from this ref inside the hints effect instead of being
  // one of its dependencies, so a re-render that only reallocates the
  // selectedRows/targets/targetIds arrays (e.g. a WebSocket-driven refresh of
  // an identical selection) never retriggers the effect - only targetIdsKey
  // (an actual change to the id set) does.
  const targetIdsRef = useRef<number[]>(targetIds);
  targetIdsRef.current = targetIds;

  // Readiness key for the current identity + selection - see HintsResolution.
  const hintsReadinessKey = `${accessIdentityKey}|${targetIdsKey}`;
  const hintsReadinessKeyRef = useRef(hintsReadinessKey);
  hintsReadinessKeyRef.current = hintsReadinessKey;

  // Per-target ping/Action-execute presentation hints, so a mixed-group user
  // (Administrator on one Well, Operator only on another) sees the right
  // enabled/disabled state per RTU instead of one global capability flag.
  // Fails closed (denied) while hints are loading, on request failure, or for
  // any selection whose hints haven't resolved yet - the UI must never show a
  // control as enabled before its target's own hint has arrived, even though
  // the backend re-checks authoritatively on every actual request regardless.
  //
  // Revalidates on accessIdentityKey/accessEvidenceVersion (the shared
  // permission-refresh signal from useCapabilityAccess), not on targetIds
  // itself, so identical hints aren't refetched purely because the selection
  // array was re-created.
  useEffect(() => {
    let cancelled = false;
    const currentTargetIds = targetIdsRef.current;
    const readinessKey = hintsReadinessKey;

    if (currentTargetIds.length === 0) {
      setHintsResolution({ key: readinessKey, status: 'ready', hints: {} });
      return undefined;
    }

    // No fresh permission evidence for this identity yet (still loading,
    // logged out, or between accounts) - fail closed instead of fetching,
    // exactly like the loading/error paths below.
    if (accessIdentityKey === null || accessEvidenceVersion === null) {
      setHintsResolution((current) => (current.key === readinessKey ? current : { key: readinessKey, status: 'loading', hints: {} }));
      return undefined;
    }

    setHintsResolution((current) => {
      if (current.key !== readinessKey) {
        return { key: readinessKey, status: 'loading', hints: {} };
      }
      // Same identity + selection as before - this run was triggered only by
      // an evidence-version change (a routine permission refresh). Keep the
      // currently displayed hints while the refetch is in flight so already-
      // authorized controls never flicker to disabled during revalidation.
      return current;
    });

    fetchRtuActionHints(currentTargetIds, accessIdentityKey)
      .then((hints) => {
        if (cancelled || hintsReadinessKeyRef.current !== readinessKey) {
          return;
        }
        setHintsResolution({ key: readinessKey, status: 'ready', hints });
      })
      .catch(() => {
        if (cancelled || hintsReadinessKeyRef.current !== readinessKey) {
          return;
        }
        setHintsResolution({ key: readinessKey, status: 'error', hints: {} });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetIdsKey, accessIdentityKey, accessEvidenceVersion]);

  // True only once hints for exactly the current identity + selection have
  // successfully resolved - false for a stale/superseded key's result (a
  // different selection or a different identity), so it can never be
  // mistaken for the current one. An identity change is reflected here in the
  // same render it happens, since hintsReadinessKey is derived synchronously
  // above - the effect only needs to run afterward to actually refetch.
  const hintsReadyForCurrentSelection = hintsResolution.key === hintsReadinessKey && hintsResolution.status === 'ready';
  const hintsUnavailableForCurrentSelection = hintsResolution.key === hintsReadinessKey && hintsResolution.status === 'error';
  const actionTargets = useMemo<ActionTargetReference[]>(
    () => targets
      .map((rtu) => ({ rtuId: rtu.id, serialNo: String(rtu.serialNo ?? '').trim() }))
      .filter((target) => Number.isInteger(target.rtuId) && target.rtuId > 0 && Boolean(target.serialNo)),
    [targets],
  );
  const missingSerialTargets = targets.filter((rtu) => !String(rtu.serialNo ?? '').trim());
  const hasActionTargets = targets.length > 0 && actionTargets.length === targets.length && missingSerialTargets.length === 0;
  const selectedRtuId = targets[0]?.id ?? null;
  const selectedRtuName = targets[0]?.name ?? null;
  const selectedRtuSerial = targets[0]?.serialNo ?? null;
  const missingSerialTargetSummary = [
    ...missingSerialTargets.slice(0, 3).map((rtu) => `RTU ${rtu.id}`),
    ...(missingSerialTargets.length > 3 ? [`+${missingSerialTargets.length - 3}`] : []),
  ].join(', ');
  const missingSerialMessage = `${t('actionsModal.missingSerial.title')}: ${missingSerialTargetSummary}`;

  useEffect(() => { window.localStorage.setItem(QUICK_ACTIONS_STORAGE_KEY, JSON.stringify(pinnedActionKeys)); }, [pinnedActionKeys]);

  const showFeedback = (title: string, message: string, kind: ActionFeedbackKind, options: Pick<FeedbackState, 'actionLabel' | 'onAction' | 'onClose'> = {}) => setFeedback({ open: true, title, message, kind, ...options });
  const closeModal = () => { if (!launching) { setBrowserUploadPercent(null); setActiveModal(null); } };
  const refreshAssetListAfterPing = useCallback(() => {
    if (!syncListStateOnPing) return;
    if (surface === 'assets') {
      dispatch(fetchRTUs({}));
      return;
    }
    // Bug 3154: manual ping completion must reconcile the currently-open RTU
    // detail view too, not only the Assets list - otherwise Basic/Detailed
    // stays stale until an unrelated WebSocket event happens to refresh it.
    if (surface === 'detail' && selectedRtuId != null) {
      dispatch(fetchRTUData(String(selectedRtuId)));
    }
  }, [dispatch, surface, syncListStateOnPing, selectedRtuId]);

  const actionErrorMessage = (action: Action): string => {
    const rejected = detailsRejected(action);
    if (rejected.length) return rejected.map((detail) => `${t('actionsModal.target', { target: detail.rtuName || detail.rtuSerialNo || detail.ip || detail.rtuId || '-' })}: ${detail.responseMessage || detail.status}`).join('\n');
    return action.actionDetails.map((detail) => detail.responseMessage).filter(Boolean).join('\n') || t('actionsModal.noTargetAccepted');
  };

  const launchErrorMessage = (error: unknown): string => {
    if (isRtuTargetIdentityChangedError(error)) {
      return t('actionsModal.targetIdentityChanged', {
        defaultValue: 'A selected RTU changed since this action was opened. Refresh the device list and try again.',
      });
    }
    return (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
      || (error as Error)?.message
      || t('actionsModal.launchFailed');
  };

  const openTracker = (action: Action, fallbackName: string) => {
    setActionId(action.id);
    setActionName(`${fallbackName} #${action.id}`);
    setActionTimestamp(new Date().toISOString());
    setProgressDialogVisible(true);
    // Lets the global activity center select+expand this action's row too,
    // so the panel already reflects the just-launched action once this
    // locally-owned dialog is closed (ACT-UI-F: "a newly launched action
    // reopens the activity center").
    dispatch(announceJustLaunchedAction(action.id));
  };

  const acceptAction = (action: Action, fallbackName: string): boolean => {
    // Clear any reconciliation "checking" feedback still on screen now that
    // the outcome is definitively known, so it never lingers under the
    // tracker or a blocked/mixed feedback dialog.
    setFeedback(EMPTY_FEEDBACK);
    const accepted = detailsAccepted(action);
    if (!accepted.length) {
      dispatch(hydrateActionSnapshots([action]));
      showFeedback(t('actionsModal.blocked.title'), actionErrorMessage(action), 'warning');
      return false;
    }
    dispatch(addAction(action));
    const rejected = detailsRejected(action);
    if (rejected.length) {
      showFeedback(t('actionsModal.mixed.title'), t('actionsModal.mixed.message', { accepted: accepted.length, blocked: rejected.length }), 'info', {
        onClose: () => openTracker(action, fallbackName),
      });
    } else {
      openTracker(action, fallbackName);
    }
    return true;
  };

  const replayPendingLaunch = async (pending: PendingLaunch): Promise<boolean> => {
    if (reconciliationInFlightRef.current) return false;
    reconciliationInFlightRef.current = true;
    setLaunching(true);
    try {
      const action = await pending.submit(pending.idempotencyKey);
      inFlightLaunchRef.current = null;
      removePendingActionLaunch(pending.idempotencyKey);
      return acceptAction(action, pending.fallbackName);
    } catch (error: unknown) {
      if (isUnknownLaunchOutcome(error)) {
        // Handoff to reconciliation rather than treating a transport timeout
        // as a failed RTU command. The key remains unchanged. Show a
        // "checking" state immediately - recoverUnknownLaunch's own retries
        // take several seconds, and the retry click already closed the prior
        // feedback dialog, so without this the screen would go blank.
        showFeedback(t('actionsModal.reconciliation.title'), t('actionsModal.reconciliation.message'), 'info');
        reconciliationInFlightRef.current = false;
        return recoverUnknownLaunch(pending.idempotencyKey, pending.fallbackName);
      }
      inFlightLaunchRef.current = null;
      removePendingActionLaunch(pending.idempotencyKey);
      showFeedback(t('actionsModal.launchFailedTitle'), launchErrorMessage(error), 'error');
      return false;
    } finally {
      reconciliationInFlightRef.current = false;
      setLaunching(false);
    }
  };

  const recoverUnknownLaunch = async (idempotencyKey: string, fallbackName: string): Promise<boolean> => {
    if (reconciliationInFlightRef.current) return false;
    reconciliationInFlightRef.current = true;
    let onlyNotFound = true;
    try {
      for (let attempt = 0; attempt < 5; attempt += 1) {
      try {
        const result = await fetchActionLaunchRequest(idempotencyKey);
        onlyNotFound = false;
        if (result.action) {
          inFlightLaunchRef.current = null;
          removePendingActionLaunch(idempotencyKey);
          return acceptAction(result.action, fallbackName);
        }
        if (String(result.state).toUpperCase() === 'REJECTED') {
          inFlightLaunchRef.current = null;
          removePendingActionLaunch(idempotencyKey);
          showFeedback(t('actionsModal.blocked.title'), result.message || t('actionsModal.launchRejected'), 'warning');
          return false;
        }
      } catch (recoveryError: unknown) {
        const status = (recoveryError as { response?: { status?: number } })?.response?.status;
        if (status && status !== 404) {
          showFeedback(t('actionsModal.reconciliation.title'), t('actionsModal.reconciliation.unavailable'), 'warning', {
            actionLabel: t('actionsModal.reconciliation.checkStatus'),
            onAction: () => { void recoverUnknownLaunch(idempotencyKey, fallbackName); },
          });
          return false;
        }
      }
      await new Promise((resolve) => window.setTimeout(resolve, 500 * (attempt + 1)));
    }
      const pending = inFlightLaunchRef.current;
      const canReplay = onlyNotFound && pending?.idempotencyKey === idempotencyKey;
      showFeedback(
        t('actionsModal.reconciliation.title'),
        canReplay ? t('actionsModal.reconciliation.notFound') : t('actionsModal.reconciliation.pending'),
        'info',
        canReplay
          ? { actionLabel: t('actionsModal.reconciliation.replay'), onAction: () => { void replayPendingLaunch(pending); } }
          : { actionLabel: t('actionsModal.reconciliation.checkStatus'), onAction: () => { void recoverUnknownLaunch(idempotencyKey, fallbackName); } },
      );
      return false;
    } finally {
      reconciliationInFlightRef.current = false;
    }
  };

  const launch = async (
    actionType: PendingActionLaunchType,
    fallbackName: string,
    requestIdentity: unknown,
    callback: (idempotencyKey: string) => Promise<Action>,
  ): Promise<boolean> => {
    if (inFlightLaunchRef.current) {
      const { idempotencyKey, fallbackName: pendingName } = inFlightLaunchRef.current;
      showFeedback(t('actionsModal.reconciliation.title'), t('actionsModal.reconciliation.pending'), 'info', {
        actionLabel: t('actionsModal.reconciliation.checkStatus'),
        onAction: () => { void recoverUnknownLaunch(idempotencyKey, pendingName); },
      });
      return false;
    }
    const requestFingerprint = createPendingActionRequestFingerprint(requestIdentity);
    const persisted = findPendingActionLaunch(actionType, actionTargets, requestFingerprint);
    if (persisted) {
      const pending: PendingLaunch = { ...persisted, submit: callback };
      inFlightLaunchRef.current = pending;
      return recoverUnknownLaunch(persisted.idempotencyKey, persisted.fallbackName);
    }
    if (!hasActionTargets) {
      showFeedback(t('actionsModal.missingSerial.title'), missingSerialMessage, 'warning');
      return false;
    }
    const idempotencyKey = createActionIdempotencyKey();
    const pending: PendingLaunch = {
      actionType,
      fallbackName,
      idempotencyKey,
      requestFingerprint,
      targets: actionTargets,
      submit: callback,
    };
    inFlightLaunchRef.current = pending;
    savePendingActionLaunch({
      actionType,
      fallbackName,
      idempotencyKey,
      requestFingerprint,
      targets: actionTargets,
      createdAtUtc: new Date().toISOString(),
    });
    setLaunching(true);
    try {
      const action = await callback(idempotencyKey);
      inFlightLaunchRef.current = null;
      removePendingActionLaunch(idempotencyKey);
      const accepted = acceptAction(action, fallbackName);
      if (accepted) setActiveModal(null);
      return accepted;
    } catch (error: unknown) {
      if (isUnknownLaunchOutcome(error)) {
        setActiveModal(null);
        // recoverUnknownLaunch's own retries (up to 5, with backoff) take
        // several seconds. Show the "checking" state up front, in the same
        // tick as closing the originating dialog, so there is never a gap
        // where nothing is visibly happening - previously the dialog closed
        // silently and no feedback appeared until reconciliation finished or
        // exhausted its retries.
        showFeedback(t('actionsModal.reconciliation.title'), t('actionsModal.reconciliation.message'), 'info');
        const accepted = await recoverUnknownLaunch(idempotencyKey, fallbackName);
        if (accepted) setActiveModal(null);
        // Close the originating dialog; reconciliation / progress owns the next step.
        return true;
      }
      inFlightLaunchRef.current = null;
      removePendingActionLaunch(idempotencyKey);
      showFeedback(t('actionsModal.launchFailedTitle'), launchErrorMessage(error), 'error');
      return false;
    } finally {
      setLaunching(false);
      setBrowserUploadPercent(null);
    }
  };

  const openAction = (key: RowActionKey) => {
    if (key === 'ping') { if (selectedRtuId) setPingDialogVisible(true); return; }
    if (key === 'generateLicence') {
      if (mode === 'bulk' || !selectedRtuId) return;
      setLicenceDialogVisible(true);
      return;
    }
    if (!hasActionTargets) {
      showFeedback(t('actionsModal.missingSerial.title'), missingSerialMessage, 'warning');
      return;
    }
    setActiveModal(key === 'updateConfig' ? 'config' : key === 'updateDeployment' ? 'deploy' : key === 'uploadLicense' ? 'license' : key === 'downloadConfig' ? 'downloadConfig' : key === 'downloadDebugLog' ? 'downloadDebugLog' : key === 'downloadDeployment' ? 'downloadDeployment' : key === 'reboot' ? 'reboot' : 'rtuInfo');
  };

  const rtuIcon = (src: string, alt: string) => <img src={src} alt={alt} style={{ width: 20, height: 20, objectFit: 'contain' }} />;
  const isImmediateSingleAction = (key: RowActionKey) => key === 'ping' || key === 'generateLicence';
  // Denied when EITHER the global role lacks the capability, OR hints for the
  // current selection haven't resolved yet (still loading, failed, or stale
  // for a superseded selection - fail closed in every case), OR any selected
  // target's own resolved hint denies it (the mixed-group case a global flag
  // alone can't express). Never enabled on the strength of the global flag
  // alone while a per-target answer is outstanding.
  const isPingDeniedForTargets = () => {
    if (!canUseDmsPing || targetIds.length === 0) {
      return !canUseDmsPing;
    }
    if (!hintsReadyForCurrentSelection) {
      return true;
    }
    return targetIds.some((id) => !hintsResolution.hints[id]?.canPing);
  };
  const isExecuteDeniedForTargets = () => {
    if (!canExecuteDmsActions || targetIds.length === 0) {
      return !canExecuteDmsActions;
    }
    if (!hintsReadyForCurrentSelection) {
      return true;
    }
    return targetIds.some((id) => !hintsResolution.hints[id]?.canExecuteActions);
  };
  const isCapabilityDenied = (key: RowActionKey) => key === 'ping' ? isPingDeniedForTargets() : isExecuteDeniedForTargets();
  // Concise, non-technical reason shown only while it's the actual cause of a
  // disabled control - never surfaces the underlying request error.
  const capabilityUnverifiedTooltip = (key: RowActionKey): string | null => {
    const hasGlobalCapability = key === 'ping' ? canUseDmsPing : canExecuteDmsActions;
    if (!hasGlobalCapability || targetIds.length === 0 || hintsReadyForCurrentSelection) {
      return null;
    }
    return hintsUnavailableForCurrentSelection
      ? t('rtuActions.accessUnverified', { defaultValue: 'Access could not be verified' })
      : t('rtuActions.accessVerifying', { defaultValue: 'Verifying access…' });
  };
  const toMenuItem = (key: RowActionKey): MenuItem => {
    const catalog = ROW_ACTION_CATALOG.find((item) => item.key === key);
    const label = catalog ? t(catalog.labelKey) : key;
    const targetingDisabled = isImmediateSingleAction(key) ? !selectedRtuId : !hasActionTargets;
    return { label, icon: catalog ? rtuIcon(catalog.icon, label) : undefined, command: () => openAction(key), disabled: isCapabilityDenied(key) || targetingDisabled };
  };
  const items: MenuItem[] = [
    { label: t('rtuActions.groups.update'), items: ['updateConfig', 'updateDeployment', 'uploadLicense'].map((key) => toMenuItem(key as RowActionKey)) },
    { label: t('rtuActions.groups.download'), items: ['downloadDebugLog', 'downloadConfig', 'downloadDeployment'].map((key) => toMenuItem(key as RowActionKey)) },
    { label: t('rtuActions.groups.other'), items: ROW_ACTION_CATALOG.filter((item) => item.group === 'other').map((item) => toMenuItem(item.key)).concat(surface === 'detail' ? [{ separator: true }, { label: t('actionsModal.quickActions.title'), command: () => setQuickActionsOpen(true) }] : []) },
  ];
  const bulkPingLabel = t('rtuActions.items.bulkPing');
  const bulkItems: MenuItem[] = [
    { label: t('rtuActions.groups.update'), items: ['updateDeployment', 'updateConfig', 'uploadLicense'].map((key) => toMenuItem(key as RowActionKey)) },
    { label: t('rtuActions.groups.download'), items: ['downloadDebugLog', 'downloadConfig', 'downloadDeployment'].map((key) => toMenuItem(key as RowActionKey)) },
    { label: t('rtuActions.groups.other'), items: [toMenuItem('reboot'), { label: bulkPingLabel, icon: rtuIcon(RTU_ICONS.PING, bulkPingLabel), command: () => setPingDialogVisible(true), disabled: targets.length < 2 || isPingDeniedForTargets() }] },
  ];
  const inlineActionKeys = useMemo(() => {
    const allowed = surface === 'detail' ? ROW_ACTION_CATALOG.map((item) => item.key) : ASSET_INLINE_ACTIONS;
    return [...pinnedActionKeys, ...DEFAULT_PINNED_ROW_ACTIONS].filter((key, index, list) => list.indexOf(key) === index && allowed.includes(key)).slice(0, surface === 'detail' ? MAX_DETAIL_INLINE_ACTIONS : ASSET_INLINE_ACTIONS.length);
  }, [pinnedActionKeys, surface]);
  const togglePinnedAction = (key: RowActionKey) => setPinnedActionKeys((current) => current.includes(key) ? current.filter((item) => item !== key) : [...current, key]);

  return <>
    <Menu model={items} popup ref={singleMenu} id="popup_menu_left" className="compact-action-menu" />
    <Menu model={bulkItems} popup ref={bulkMenu} id="bulk_menu" className="compact-action-menu" />
    {mode === 'bulk' ? <Tooltip title={missingSerialTargets.length ? missingSerialMessage : t('rtuActions.bulkActions')}><span><Button type="button" icon="pi pi-ellipsis-v" label={t('rtuActions.bulkActions')} size="small" className="rtu-bulk-actions-button" style={{ backgroundColor: defaultThemeColor, borderColor: defaultThemeColor, color: 'white', fontSize: '0.75rem', padding: '0.35rem 0.6rem', height: '1.75rem' }} outlined={targets.length < 2} raised={targets.length >= 2} onClick={(event) => bulkMenu.current?.toggle(event)} disabled={targets.length < 2} aria-controls="bulk_menu" aria-haspopup /></span></Tooltip> : <div className={`rtu-inline-actions${surface === 'detail' ? ' rtu-inline-actions--detail' : ''}`} data-testid="rtu-inline-actions">
      {inlineActionKeys.map((key) => { const catalog = ROW_ACTION_CATALOG.find((item) => item.key === key); const label = catalog ? t(catalog.labelKey) : key; const targetingDisabled = isImmediateSingleAction(key) ? !selectedRtuId : !hasActionTargets; const unverifiedTooltip = capabilityUnverifiedTooltip(key); const tooltipTitle = !hasActionTargets && !isImmediateSingleAction(key) ? missingSerialMessage : unverifiedTooltip ?? label; return <Tooltip title={tooltipTitle} key={key}><span><button type="button" className="rtu-inline-action-button" onClick={() => openAction(key)} disabled={isCapabilityDenied(key) || targetingDisabled} aria-label={label}><img src={catalog?.icon || RTU_ICONS.PING} alt="" /></button></span></Tooltip>; })}
      <IconActionMenuButton onClick={(event) => singleMenu.current?.toggle(event)} tooltip={t('table.moreActions')} />
    </div>}

    <ActionConfirmationModal open={activeModal === 'reboot'} title={t('rtuActions.dialogs.rebootConfirm.title')} description={t(mode === 'bulk' ? 'rtuActions.dialogs.rebootConfirm.bulkMessage' : 'rtuActions.dialogs.rebootConfirm.singleMessage')} confirmLabel={t('rtuActions.dialogs.rebootConfirm.confirmLabel')} loading={launching} onCancel={closeModal} onConfirm={(comment) => void launch('REBOOT', t('rtuActions.progress.reboot'), { value: 6420, comment }, (key) => launchRebootRtus(actionTargets, { val: 6420, comment, idempotencyKey: key }))} />
    <ActionConfirmationModal open={activeModal === 'rtuInfo'} title={t('rtuActions.items.rtuInfo')} description={t('actionsModal.rtuInfoDescription')} confirmLabel={t('actionsModal.start')} loading={launching} onCancel={closeModal} onConfirm={(comment) => void launch('RTU_INFO', t('rtuActions.progress.rtuInfo'), { comment }, (key) => launchRtuInfoRtus(actionTargets, { comment, idempotencyKey: key }))} />
    <UploadActionModal open={activeModal === 'config'} title={t(mode === 'bulk' ? 'rtuActions.fileDialog.configBulk' : 'rtuActions.fileDialog.configSingle')} description={t('actionsModal.targetsSelected', { count: targets.length })} extension=".cfg" busy={launching} uploadPercent={browserUploadPercent} onCancel={closeModal} onLaunch={(file, comment, run) => { setBrowserUploadPercent(0); void launch('UPDATE_CONFIG', t('rtuActions.progress.updateConfig'), { file: fileRequestIdentity(file), run, comment }, (key) => launchUploadConfig(file, actionTargets, { run: run ? 1 : 0, comment, idempotencyKey: key, onUploadProgress: setBrowserUploadPercent })); }} />
    <UploadActionModal open={activeModal === 'license'} title={t(mode === 'bulk' ? 'rtuActions.fileDialog.licenseBulk' : 'rtuActions.fileDialog.licenseSingle')} description={t('actionsModal.targetsSelected', { count: targets.length })} extension=".lic" showRun={false} busy={launching} uploadPercent={browserUploadPercent} onCancel={closeModal} onLaunch={(file, comment) => { setBrowserUploadPercent(0); void launch('UPDATE_LICENSE', t('rtuActions.progress.uploadLicense'), { file: fileRequestIdentity(file), comment }, (key) => launchUploadLicense(file, actionTargets, { comment, idempotencyKey: key, onUploadProgress: setBrowserUploadPercent })); }} />
    <DeploymentUpdateModal open={activeModal === 'deploy'} targets={targets} busy={launching} uploadPercent={browserUploadPercent} onCancel={closeModal} onLaunch={({ file, dataPreserve, cfgPreserve, run, comment }) => { setBrowserUploadPercent(0); void launch('UPDATE_DEPLOY', t('rtuActions.progress.updateDeployment'), { file: fileRequestIdentity(file), dataPreserve, cfgPreserve, run, comment }, (key) => launchUploadDeployment(file, actionTargets, { data_preserve: dataPreserve ? 1 : 0, cfg_preserve: cfgPreserve ? 1 : 0, run: run ? 1 : 0, comment, idempotencyKey: key, onUploadProgress: setBrowserUploadPercent })); }} />
    <DownloadActionModal open={activeModal === 'downloadConfig'} kind="config" targets={targets} title={t('rtuActions.downloadDialog.configTitle')} description={t('actionsModal.targetsSelected', { count: targets.length })} busy={launching} onCancel={closeModal} onLaunch={({ comment, save, selection, filename }) => { const configSelection = selection === 'EXISTING' ? 'EXISTING' : 'CURRENT'; void launch('DOWNLOAD_CONFIG', t('rtuActions.progress.downloadConfig'), { selection: configSelection, filename, save: Boolean(save), comment }, (key) => launchConfigDownload(actionTargets, { comment, selection: configSelection, filename, save: Boolean(save), idempotencyKey: key })); }} />
    <DownloadActionModal open={activeModal === 'downloadDebugLog'} kind="debugLog" title={t('rtuActions.downloadDialog.debugLogTitle')} description={t('actionsModal.targetsSelected', { count: targets.length })} busy={launching} onCancel={closeModal} onLaunch={({ comment }) => void launch('RTU_DEBUG_LOG', t('rtuActions.progress.downloadDebugLog'), { comment }, (key) => launchRtuDebugLogDownload(actionTargets, { comment, idempotencyKey: key }))} />
    <DownloadActionModal open={activeModal === 'downloadDeployment'} kind="deployment" targets={targets} title={t('rtuActions.downloadDialog.deploymentTitle')} description={t('actionsModal.targetsSelected', { count: targets.length })} busy={launching} onCancel={closeModal} onLaunch={({ comment, selection, filename }) => void launch('DOWNLOAD_DEPLOY', t('rtuActions.progress.downloadDeployment'), { comment, selection: selection || 'LATEST', filename }, (key) => launchDeploymentDownload(actionTargets, { comment, selection: (selection === 'EXISTING' ? 'EXISTING' : 'LATEST'), filename, idempotencyKey: key }))} />
    <QuickActionsModal open={quickActionsOpen} onClose={() => setQuickActionsOpen(false)}><div className="dms-action-modal__stack"><Typography.Paragraph>{t('rtuActions.quickActions.description')}</Typography.Paragraph><List dataSource={ROW_ACTION_CATALOG} renderItem={(item) => <List.Item><Checkbox checked={pinnedActionKeys.includes(item.key)} onChange={() => togglePinnedAction(item.key)}>{t(item.labelKey)}</Checkbox></List.Item>} /></div></QuickActionsModal>
    <ActionFeedbackModal
      {...feedback}
      onAction={feedback.onAction ? () => {
        // Recovery can immediately open progress. Clear this feedback surface
        // first so users never see two action modals at once.
        const onAction = feedback.onAction;
        setFeedback(EMPTY_FEEDBACK);
        onAction?.();
      } : undefined}
      onClose={() => { const onClose = feedback.onClose; setFeedback(EMPTY_FEEDBACK); onClose?.(); }}
    />
    {actionId !== null ? <ProgressDialog key={actionTimestamp} actionId={actionId} rtus={targets} actionName={actionName} actionTimestamp={actionTimestamp} visible={progressDialogVisible} onHide={() => setProgressDialogVisible(false)} /> : null}
    <RtuPingDialog visible={pingDialogVisible} rtuId={mode === 'bulk' ? null : selectedRtuId} rtuIds={mode === 'bulk' ? targets.map((target) => target.id) : []} selectedRtus={targets} rtuName={mode === 'bulk' ? null : selectedRtuName} onCompleted={refreshAssetListAfterPing} onHide={() => setPingDialogVisible(false)} />
    <RtuGenerateLicenceDialog
      visible={licenceDialogVisible && mode !== 'bulk'}
      rtuId={selectedRtuId}
      rtuName={selectedRtuName}
      rtuSerial={selectedRtuSerial}
      canUploadToRtu={hasActionTargets}
      uploadingToRtu={launching && licenceDialogVisible}
      onHide={() => setLicenceDialogVisible(false)}
      onUploadToRtu={async (file) => {
        setBrowserUploadPercent(0);
        const accepted = await launch(
          'UPDATE_LICENSE',
          t('rtuActions.progress.uploadLicense'),
          { file: fileRequestIdentity(file), comment: '', source: 'generateLicenceDialog' },
          (key) => launchUploadLicense(file, actionTargets, {
            comment: '',
            idempotencyKey: key,
            onUploadProgress: setBrowserUploadPercent,
          }),
        );
        if (accepted) {
          setLicenceDialogVisible(false);
        }
        return accepted;
      }}
    />
  </>;
};

export default RTUActions;
